<script setup lang="ts">
import { passwordRegex } from 'assets/data/config';
import ExclamationIcon from '../../design-system/icons/ExclamationIcon.vue';
import { CheckCircleIcon, ChevronUpIcon } from '@heroicons/vue/24/outline';
import { generateTranslationKey } from '~/utilities/data-transformers';
import FloatLabel from 'primevue/floatlabel';
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/solid';
import CircleIcon from '~/components/design-system/icons/CircleIcon.vue';
import Password from 'primevue/password';
import type { IElement, TTemplateName } from '~/interfaces/form/formBuilder';
defineProps<{
  element: IElement;
  formTitle: TTemplateName;
  formErrors: { [key: string]: string };
}>();
const emits = defineEmits<{
  (e: 'inputChange', id: string): void;
}>();
const model = defineModel<string>();
const touched = ref<boolean>(false);
const passwordSuggestions = usePasswordSuggestions();
const site = useSiteStore();
const regionalPasswordRegex = computed(
  () => site.getConfig?.devConfig?.passwordReg,
);
watchEffect(() =>
  passwordSuggestions.forEach((ps) => {
    const regex = new RegExp(ps.regex[0], ps?.regex[1] ? ps.regex[1] : '');
    ps.display = !!model.value && regex.test(String(model.value));
  }),
);
</script>
<template>
  <div class="relative">
    <FloatLabel
      v-if="!!element && !!formErrors"
      :id="`${element.id.replace(' ', '')}-label`"
      :touched="touched || !!model"
      variant="in"
    >
      <Password
        v-model="model"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        append-to="body"
        :input-id="element.id"
        :type="element.type"
        :aria-label="element.label"
        :required="element.required"
        :invalid="!!formErrors[element.id]"
        :name="element.id"
        :medium-regex="
          regionalPasswordRegex
            ? regionalPasswordRegex.medium
            : passwordRegex[site.getRegionCode].medium
        "
        :strong-regex="
          regionalPasswordRegex
            ? regionalPasswordRegex.strong
            : passwordRegex[site.getRegionCode].strong
        "
        :form-title="formTitle"
        toggle-mask
        show-icon
        @click="touched = true"
        @focus="touched = true"
        @blur="touched = false"
        @value-change="emits('inputChange', element.id)"
      >
        <template #unmaskicon="{ toggleCallback }">
          <EyeIcon
            class="text-dark-600 dark:text-light-50 mr-1 w-6 right-1 absolute top-[12px]"
            @click="toggleCallback"
          />
        </template>
        <template #maskicon="{ toggleCallback }">
          <EyeSlashIcon
            class="text-dark-600 dark:text-light-50 mr-1 w-6 right-1 absolute top-[12px]"
            @click="toggleCallback"
          />
        </template>
        <template #header>
          <div class="flex items-center justify-between">
            <p
              class="my-0 py-2 pl-2 font-bold text-sm capitalize dark:text-light-50 text-dark-700"
            >
              {{ $t('password-validity') }}
            </p>
            <ChevronUpIcon class="w-4 text-sm mr-2 close-popup" />
          </div>
        </template>
        <template #footer>
          <hr class="pl-0 w-full std-border" />
          <div class="pl-2">
            <p
              class="mt-2 mb-1 text-sm font-bold capitalize dark:text-light-50 text-dark-700"
            >
              {{ $t('required') }}
            </p>
            <ul
              class="pl-0 ml-0 mt-0 list-none dark:text-light-50 text-dark-700 mb-2"
              style="line-height: 1.5"
            >
              <div
                v-for="(ps, index) in passwordSuggestions"
                :key="index"
                class="flex items-center flex-wrap"
              >
                <component
                  :is="ps.icon"
                  v-if="ps.display"
                  :key="index"
                  class="mr-2 w-5"
                  :class="`text-${ps.colour}`"
                  :tooltip="
                    toRaw(ps.icon) === ExclamationIcon
                      ? 'password-contains-password'
                      : ''
                  "
                />
                <CircleIcon
                  v-else-if="ps.icon === CheckCircleIcon"
                  class="mr-2 w-5"
                />
                <li
                  v-if="ps.display || ps.icon === CheckCircleIcon"
                  class="text-sm text-dark-700 dark:text-light-50"
                >
                  {{ ps.text }}
                </li>
                <!-- component and colour depending on suggestion/warning/error -->
              </div>
            </ul>
          </div>
        </template>
      </Password>
      <label :for="element.id">
        {{
          $t(
            `label.${generateTranslationKey(element.label)}-${site.getRegionCode}`,
            `label.${generateTranslationKey(element.label)}`,
          )
        }}
      </label>
    </FloatLabel>
  </div>
</template>
